import { useState, useEffect, useCallback } from 'react'
import { writingAssistantService } from '../../services/writingAssistantService'
import type { WritingAssistantListItem } from '../../types/writing'

interface UseWritingAssistantTranslationProps {
  writingAssistants: WritingAssistantListItem[]
  updateUnreadyWritingAssistants: (
    translatingIds?: string[],
    forceUpdateIds?: string[]
  ) => Promise<void>
  isClosedCrisis: boolean
}

export function useWritingAssistantTranslation({
  writingAssistants,
  updateUnreadyWritingAssistants,
  isClosedCrisis,
}: UseWritingAssistantTranslationProps) {
  const [selectedLanguages, setSelectedLanguages] = useState<Record<string, string>>({})
  const [translatingStates, setTranslatingStates] = useState<Record<string, string>>({})

  // Initialize selected languages to Dutch by default
  useEffect(() => {
    setSelectedLanguages(prev => {
      const newLanguages = { ...prev }
      writingAssistants.forEach(wa => {
        if (!newLanguages[wa.id]) {
          newLanguages[wa.id] = 'nl'
        }
      })
      return newLanguages
    })
  }, [writingAssistants])

  // Check if translation needs to be generated
  const needsTranslation = useCallback(
    (writingAssistant: WritingAssistantListItem, targetLanguage: string): boolean => {
      const translations = writingAssistant.translations || {}
      const existingTranslation = translations[targetLanguage]

      if (!existingTranslation) return true

      // Check version match
      const currentVersion = (writingAssistant.versionHistory?.length || 0) + 1
      if (
        typeof existingTranslation === 'object' &&
        existingTranslation.version === currentVersion
      ) {
        return false
      }

      return true
    },
    []
  )

  // Handle translation request
  const handleTranslate = useCallback(
    async (writingAssistantId: string, targetLanguage: string) => {
      if (isClosedCrisis) return

      try {
        const response = await writingAssistantService.translateWritingAssistant(
          writingAssistantId,
          targetLanguage
        )

        // If already translated, clear the translating state and update
        if (response.data.status === 'already_translated') {
          setTranslatingStates(prev => {
            const newState = { ...prev }
            delete newState[writingAssistantId]
            return newState
          })
          // Force update this specific writing assistant
          await updateUnreadyWritingAssistants([], [writingAssistantId])
        }
      } catch (err) {
        console.error('Error translating:', err)
        // Clear translating state on error
        setTranslatingStates(prev => {
          const newState = { ...prev }
          delete newState[writingAssistantId]
          return newState
        })
      }
    },
    [isClosedCrisis, updateUnreadyWritingAssistants]
  )

  // Handle language change
  const handleLanguageChange = useCallback(
    async (writingAssistantId: string, targetLanguage: string) => {
      setSelectedLanguages(prev => ({ ...prev, [writingAssistantId]: targetLanguage }))

      // If switching to a non-Dutch language and translation is needed
      if (targetLanguage !== 'nl') {
        const wa = writingAssistants.find(w => w.id === writingAssistantId)
        if (wa && needsTranslation(wa, targetLanguage)) {
          // Set translating state immediately to show loading
          setTranslatingStates(prev => ({ ...prev, [writingAssistantId]: targetLanguage }))
          await handleTranslate(writingAssistantId, targetLanguage)
        }
      }
    },
    [writingAssistants, needsTranslation, handleTranslate]
  )

  // Poll for translation updates
  useEffect(() => {
    const hasTranslatingWA = Object.keys(translatingStates).length > 0

    if (hasTranslatingWA) {
      const intervalId = setInterval(async () => {
        await updateUnreadyWritingAssistants(Object.keys(translatingStates))

        // Check if translations are complete and clear translating states
        Object.keys(translatingStates).forEach(waId => {
          const wa = writingAssistants.find(w => w.id === waId)
          const targetLang = translatingStates[waId]

          if (wa && !needsTranslation(wa, targetLang)) {
            // Translation is complete
            setTranslatingStates(prev => {
              const newState = { ...prev }
              delete newState[waId]
              return newState
            })
          }
        })
      }, 2000)

      return () => clearInterval(intervalId)
    }
    return undefined
  }, [translatingStates, writingAssistants, updateUnreadyWritingAssistants, needsTranslation])

  return {
    selectedLanguages,
    translatingStates,
    handleLanguageChange,
    needsTranslation,
  }
}
